/**
 * ServiceAProgressPanel - Client-facing progress view for Service A (Discovery & Viability Assessment)
 * 
 * Phase 4: User Portal Transparency - Workflow Engine Integration
 * Phase 6: Stripe Integration + Payment Processing States
 * 
 * Design principles:
 * - Calm progress indicators (airport departure board, not engineering console)
 * - Real milestones from canonical engagement record
 * - Discovery call status with meeting details
 * - Artifacts vault preview
 * - What the client can expect next
 * - Zero internal jargon
 * - Zero AI hype
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  Download,
  Lock,
  Info,
  Loader2,
  CreditCard,
  AlertCircle,
  Calendar,
  Video,
  ExternalLink,
  MessageSquare
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// User-facing payment status messages (Phase 6 canonical copy)
const PAYMENT_STATUS_COPY = {
  processing: {
    title: "Payment Processing",
    message: "Your payment is being processed. This usually takes just a moment.",
    subtext: "Please don't refresh the page."
  },
  pending: {
    title: "Awaiting Confirmation",
    message: "We're waiting for payment confirmation from our payment processor.",
    subtext: "This can take up to 30 seconds. You'll be notified once confirmed."
  },
  failed: {
    title: "Payment Issue",
    message: "There was an issue processing your payment. Please try again.",
    subtext: "If the problem persists, please contact support."
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WorkflowMilestoneItem - Individual milestone from workflow engine
 */
const WorkflowMilestoneItem = ({ milestone, isLast }) => {
  const statusConfig = {
    DONE: {
      icon: <CheckCircle2 className="w-5 h-5 text-green-400" />,
      badge: "bg-green-500/20 text-green-400 border-green-500/30",
      label: "Complete"
    },
    READY: {
      icon: <Clock className="w-5 h-5 text-blue-400" />,
      badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      label: "Ready"
    },
    IN_PROGRESS: {
      icon: <Clock className="w-5 h-5 text-blue-400 animate-pulse" />,
      badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      label: "In Progress"
    },
    LOCKED: {
      icon: <Lock className="w-5 h-5 text-slate-600" />,
      badge: "bg-slate-700/50 text-slate-500 border-slate-600/30",
      label: "Upcoming"
    },
    BLOCKED: {
      icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
      badge: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      label: "Needs Attention"
    }
  };

  const config = statusConfig[milestone.status] || statusConfig.LOCKED;

  return (
    <div className="flex gap-4 relative" data-testid={`milestone-${milestone.code}`}>
      {/* Vertical connector line */}
      {!isLast && (
        <div className="absolute left-[9px] top-8 bottom-0 w-0.5 bg-slate-700" />
      )}
      
      {/* Status icon */}
      <div className="flex-shrink-0 z-10 bg-navy-900">
        {config.icon}
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className={`font-medium text-sm ${milestone.status === 'LOCKED' ? 'text-slate-500' : 'text-white'}`}>
            {milestone.title}
          </h4>
          <Badge className={`text-xs ${config.badge}`}>
            {config.label}
          </Badge>
        </div>
        {milestone.completed_at && (
          <p className="text-slate-500 text-xs">
            Completed {new Date(milestone.completed_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * NextStepPanel - Prominent "Next Step" CTA for post-payment users
 */
const NextStepPanel = ({ nextAction, schedulingAvailable, discoveryCall, onRequestCall }) => {
  // Map next action codes to UI content
  const actionContent = {
    SCHEDULE_DISCOVERY_CALL: {
      icon: <Calendar className="w-6 h-6 text-primary" />,
      title: "Next Step",
      primary: "Schedule your Discovery Call",
      description: "This call confirms your goals and kicks off your engagement."
    },
    AWAIT_QUESTIONNAIRE: {
      icon: <FileText className="w-6 h-6 text-primary" />,
      title: "In Progress",
      primary: "Your questionnaire is being prepared",
      description: "Our team is creating a customized discovery questionnaire for you."
    },
    COMPLETE_INTERVIEW: {
      icon: <Video className="w-6 h-6 text-primary" />,
      title: "Next Step",
      primary: "Complete your Discovery Interview",
      description: "Join your scheduled call to discuss your business needs."
    },
    AWAIT_ANALYSIS: {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: "In Progress",
      primary: "Analysis underway",
      description: "Our team is analyzing your information and preparing insights."
    },
    VIEW_REPORT: {
      icon: <FileText className="w-6 h-6 text-green-400" />,
      title: "Ready",
      primary: "Your report is ready",
      description: "Review your consultation findings and recommendations."
    },
    SIGN_OFF: {
      icon: <CheckCircle2 className="w-6 h-6 text-green-400" />,
      title: "Final Step",
      primary: "Confirm delivery",
      description: "Review and confirm that you have received all deliverables."
    },
    NONE: {
      icon: <CheckCircle2 className="w-6 h-6 text-green-400" />,
      title: "Complete",
      primary: "Engagement complete",
      description: "Thank you for choosing AI Means Business."
    }
  };

  const content = actionContent[nextAction?.code] || actionContent.NONE;
  
  // Don't show for completed engagements
  if (nextAction?.code === "NONE") {
    return null;
  }

  return (
    <Card 
      className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30"
      data-testid="next-step-panel"
    >
      <CardContent className="flex items-start gap-4 py-5">
        {/* Icon */}
        <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
          {content.icon}
        </div>
        
        {/* Content */}
        <div className="flex-1">
          <h3 className="text-white font-semibold text-lg mb-1">
            {content.title}
          </h3>
          <p className="text-white text-base font-medium mb-2">
            {content.primary}
          </p>
          <p className="text-slate-400 text-sm mb-4">
            {content.description}
          </p>
          
          {/* CTA for scheduling */}
          {nextAction?.code === "SCHEDULE_DISCOVERY_CALL" && (
            <>
              <Button
                disabled={!schedulingAvailable}
                className={schedulingAvailable 
                  ? "bg-primary hover:bg-primary/90" 
                  : "bg-slate-600 cursor-not-allowed opacity-80"
                }
                data-testid="schedule-discovery-call-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {schedulingAvailable ? "Schedule Now" : "Scheduling Coming Soon"}
              </Button>
              
              {!schedulingAvailable && (
                <div className="mt-3">
                  <p className="text-slate-500 text-xs mb-2">
                    Scheduling will be available shortly. We&apos;ll email you when it&apos;s live.
                  </p>
                  {onRequestCall && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRequestCall}
                      className="text-xs"
                      data-testid="request-call-btn"
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Request a Call Time
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * DiscoveryCallCard - Shows discovery call status and details
 */
const DiscoveryCallCard = ({ discoveryCall }) => {
  if (!discoveryCall || discoveryCall.status === "NOT_SCHEDULED") {
    return null;
  }

  const isScheduled = discoveryCall.status === "SCHEDULED";
  const isCompleted = discoveryCall.status === "COMPLETED";
  
  const formattedDate = discoveryCall.scheduled_time 
    ? new Date(discoveryCall.scheduled_time).toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : null;

  return (
    <Card className="bg-navy-900/50 border-slate-800" data-testid="discovery-call-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          Discovery Call
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-slate-400 text-sm">Status</span>
          <Badge className={isCompleted 
            ? "bg-green-500/20 text-green-400 border-green-500/30"
            : "bg-blue-500/20 text-blue-400 border-blue-500/30"
          }>
            {isCompleted ? "Completed" : "Scheduled"}
          </Badge>
        </div>
        
        {formattedDate && (
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Date & Time</span>
            <span className="text-white text-sm">{formattedDate}</span>
          </div>
        )}
        
        {isScheduled && discoveryCall.meeting_link && (
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={() => window.open(discoveryCall.meeting_link, '_blank')}
            data-testid="join-meeting-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Join Meeting
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * ArtifactsVaultCard - Shows engagement artifacts
 */
const ArtifactsVaultCard = ({ artifacts }) => {
  if (!artifacts || artifacts.length === 0) {
    return null;
  }

  const artifactLabels = {
    QUESTIONNAIRE_PDF: "Discovery Questionnaire",
    TRANSCRIPT: "Interview Transcript",
    FINAL_REPORT_PDF: "Final Report",
    DISCOVERY_SUMMARY: "Discovery Summary",
    CERTIFICATE: "Certificate of Completion",
    NOTES: "Notes"
  };

  const artifactIcons = {
    QUESTIONNAIRE_PDF: <FileText className="w-4 h-4" />,
    TRANSCRIPT: <MessageSquare className="w-4 h-4" />,
    FINAL_REPORT_PDF: <FileText className="w-4 h-4" />,
    DISCOVERY_SUMMARY: <FileText className="w-4 h-4" />,
    CERTIFICATE: <CheckCircle2 className="w-4 h-4" />,
    NOTES: <MessageSquare className="w-4 h-4" />
  };

  // Filter out placeholder/draft artifacts that have no content
  const visibleArtifacts = artifacts.filter(a => 
    a.status === "GENERATED" || a.status === "READY" || a.has_content
  );

  if (visibleArtifacts.length === 0) {
    return null;
  }

  return (
    <Card className="bg-navy-900/50 border-slate-800" data-testid="artifacts-vault-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Your Documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {visibleArtifacts.map((artifact) => (
            <div 
              key={artifact.artifact_id}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50"
              data-testid={`artifact-${artifact.type}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-slate-400">
                  {artifactIcons[artifact.type] || <FileText className="w-4 h-4" />}
                </span>
                <span className="text-white text-sm">
                  {artifactLabels[artifact.type] || artifact.type}
                </span>
              </div>
              
              {artifact.download_url ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(artifact.download_url, '_blank')}
                >
                  <Download className="w-4 h-4" />
                </Button>
              ) : (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                  Ready
                </Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * RequestCallModal - Simple modal for requesting a call
 */
const RequestCallModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [preferredTimes, setPreferredTimes] = useState("");
  const [notes, setNotes] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ preferredTimes, notes });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <Card className="bg-navy-900 border-slate-700 max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-white">Request a Call Time</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm block mb-1">
                What times work best for you?
              </label>
              <textarea
                value={preferredTimes}
                onChange={(e) => setPreferredTimes(e.target.value)}
                placeholder="e.g., Weekdays 2-5pm EST, or Tuesday/Thursday mornings"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm resize-none h-24"
                required
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm block mb-1">
                Additional notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any other preferences or requirements"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm resize-none h-20"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !preferredTimes.trim()}
                className="flex-1"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Send Request
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * ServiceAProgressPanel - Main component
 */
const ServiceAProgressPanel = ({ serId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [serviceData, setServiceData] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [error, setError] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [initiatingCheckout, setInitiatingCheckout] = useState(false);
  const [servicePrice, setServicePrice] = useState({ price: 997, display: "$997" });
  const [schedulingData, setSchedulingData] = useState({ available: false, fallback: null });
  const [showRequestCallModal, setShowRequestCallModal] = useState(false);
  const [submittingCallRequest, setSubmittingCallRequest] = useState(false);

  // Fetch dynamic pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/public/service-prices`);
        if (response.data?.SERVICE_A) {
          setServicePrice(response.data.SERVICE_A);
        }
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
      }
    };
    fetchPricing();
  }, []);

  // Fetch scheduling availability
  useEffect(() => {
    const fetchSchedulingAvailability = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/scheduling/slots`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSchedulingData({
          available: response.data?.available || false,
          fallback: response.data?.fallback || null
        });
      } catch (err) {
        console.error("Failed to fetch scheduling availability:", err);
        setSchedulingData({ available: false, fallback: null });
      }
    };
    
    // Fetch when we have paid engagement
    if (workflowData?.payment_status === "SUCCEEDED" || serviceData?.ser?.payment?.status === "PAID") {
      fetchSchedulingAvailability();
    }
  }, [token, workflowData?.payment_status, serviceData?.ser?.payment?.status]);

  // Fetch workflow engagement data (new canonical API)
  const fetchWorkflowData = useCallback(async () => {
    if (!serId || !token) return;
    
    try {
      const response = await axios.get(`${API}/workflow/user/engagement/${serId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflowData(response.data);
    } catch (err) {
      // Workflow data might not exist yet for new engagements
      console.log("Workflow data not available yet:", err.response?.status);
      setWorkflowData(null);
    }
  }, [serId, token]);

  // Fetch legacy service data (for backward compatibility)
  const fetchServiceAStatus = useCallback(async () => {
    if (!serId || !token) return;
    
    try {
      const response = await axios.get(`${API}/user/engagement/${serId}/service-a`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceData(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch Service A status:", err);
      setError("Unable to load engagement status");
    } finally {
      setLoading(false);
    }
  }, [serId, token]);

  useEffect(() => {
    fetchServiceAStatus();
    fetchWorkflowData();
  }, [fetchServiceAStatus, fetchWorkflowData]);
  
  // URL params for payment return
  const urlParams = new URLSearchParams(window.location.search);
  const paymentReturn = urlParams.get("payment");
  const sessionIdFromUrl = urlParams.get("session_id");
  
  // Derived payment state
  const paymentStatus = workflowData?.payment_status || serviceData?.ser?.payment?.status || "UNPAID";
  const isPaid = paymentStatus === "PAID" || paymentStatus === "SUCCEEDED";
  const isPaymentPending = paymentStatus === "PENDING";
  const isPaymentFailed = paymentStatus === "FAILED";
  const agreementAccepted = Boolean(serviceData?.ser?.agreement?.accepted_at);
  
  // Poll for payment confirmation after Stripe return
  useEffect(() => {
    if (paymentReturn === "success" && sessionIdFromUrl && !isPaid && token && serId) {
      setPollingPayment(true);
      
      const pollPaymentStatus = async (attempts = 0) => {
        if (attempts >= 10) {
          setPollingPayment(false);
          toast.info("Payment confirmation is taking longer than expected. Please check back shortly.");
          return;
        }
        
        try {
          const response = await axios.get(`${API}/user/engagement/${serId}/service-a`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const newPaymentStatus = response.data.ser?.payment?.status;
          if (newPaymentStatus === "PAID" || newPaymentStatus === "SUCCEEDED") {
            setPollingPayment(false);
            setServiceData(response.data);
            // Also fetch workflow data
            fetchWorkflowData();
            toast.success("Payment confirmed! Thank you for your purchase.");
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
          
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        } catch (err) {
          console.error("Payment poll error:", err);
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      };
      
      pollPaymentStatus();
    }
  }, [paymentReturn, sessionIdFromUrl, isPaid, serId, token, fetchWorkflowData]);
  
  // Handle payment cancelled
  useEffect(() => {
    if (paymentReturn === "cancelled") {
      toast.info("Payment was cancelled. You can try again when ready.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [paymentReturn]);
  
  // Initiate Stripe checkout
  const handlePayNow = async () => {
    setInitiatingCheckout(true);
    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/portal?ser_id=${serId}&payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/portal?ser_id=${serId}&payment=cancelled`;
      
      const response = await axios.post(
        `${API}/ser/${serId}/service-a/stripe/checkout-session`,
        { 
          success_url: successUrl,
          cancel_url: cancelUrl
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Unable to create checkout session");
        setInitiatingCheckout(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === "object") {
        toast.error(errorDetail.message || "Failed to initiate payment");
      } else {
        toast.error(errorDetail || "Failed to initiate payment. Please try again.");
      }
      setInitiatingCheckout(false);
    }
  };

  // Handle call request submission
  const handleRequestCall = async ({ preferredTimes, notes }) => {
    setSubmittingCallRequest(true);
    try {
      await axios.post(
        `${API}/workflow/user/engagement/${serId}/request-call`,
        { preferred_times: preferredTimes, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Call request sent! We'll contact you shortly.");
      setShowRequestCallModal(false);
    } catch (err) {
      console.error("Request call error:", err);
      toast.error("Failed to send request. Please try again.");
    } finally {
      setSubmittingCallRequest(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className="bg-navy-900/50 border-slate-800">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error || !serviceData) {
    return (
      <Card className="bg-navy-900/50 border-slate-800">
        <CardContent className="text-center py-8">
          <Info className="w-8 h-8 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400">We&apos;re preparing your next steps. Refresh in a moment.</p>
        </CardContent>
      </Card>
    );
  }

  // Use workflow data if available, fallback to legacy
  const milestones = workflowData?.milestones || [];
  const discoveryCall = workflowData?.discovery_call || {};
  const artifacts = workflowData?.artifacts_summary || [];
  const nextAction = workflowData?.next_action || serviceData?.next_action || {};
  const engagementStatus = workflowData?.engagement_status || serviceData?.ser?.lifecycle_state;
  
  // Get service name from legacy data
  const serviceName = serviceData?.ser?.service_id === "SERVICE_B" 
    ? "A.I. Readiness Consultation" 
    : "Discovery & Viability Assessment";

  return (
    <div className="space-y-6" data-testid="service-a-progress-panel">
      {/* Next Step Panel */}
      <NextStepPanel 
        nextAction={nextAction}
        schedulingAvailable={schedulingData.available}
        discoveryCall={discoveryCall}
        onRequestCall={() => setShowRequestCallModal(true)}
      />

      {/* Service Overview Header */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {serviceName}
              </CardTitle>
              <Badge className={`mt-2 ${isPaid 
                ? "bg-green-500/20 text-green-400 border-green-500/30" 
                : "bg-primary/20 text-primary border-primary/30"}`}
              >
                {isPaid ? "Payment Confirmed – Active" : "Pending Payment"}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm leading-relaxed">
            A focused consultation to assess your business&apos;s A.I. readiness and identify practical opportunities for integration.
          </p>
        </CardContent>
      </Card>

      {/* Payment Processing Banner */}
      {(pollingPayment || isPaymentPending) && !isPaid && (
        <Card className="bg-blue-500/5 border-blue-500/30">
          <CardContent className="flex items-center gap-4 py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
            <div>
              <h4 className="text-white font-medium">{PAYMENT_STATUS_COPY.processing.title}</h4>
              <p className="text-slate-400 text-sm">{PAYMENT_STATUS_COPY.processing.message}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Failed Banner */}
      {isPaymentFailed && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardContent className="flex items-center gap-4 py-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <div className="flex-1">
              <h4 className="text-white font-medium">{PAYMENT_STATUS_COPY.failed.title}</h4>
              <p className="text-slate-400 text-sm">{PAYMENT_STATUS_COPY.failed.message}</p>
            </div>
            <Button
              onClick={handlePayNow}
              disabled={initiatingCheckout}
              className="bg-red-600 hover:bg-red-700"
            >
              {initiatingCheckout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Retry Payment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Discovery Call Card (if scheduled) */}
      <DiscoveryCallCard discoveryCall={discoveryCall} />

      {/* Milestone Progress (only show if we have workflow data and user is paid) */}
      {isPaid && milestones.length > 0 && (
        <Card className="bg-navy-900/50 border-slate-800" data-testid="milestones-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Your Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {milestones.map((milestone, idx) => (
                <WorkflowMilestoneItem
                  key={milestone.code}
                  milestone={milestone}
                  isLast={idx === milestones.length - 1}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Artifacts Vault Card */}
      <ArtifactsVaultCard artifacts={artifacts} />

      {/* Pre-payment: Show payment CTA */}
      {!isPaid && agreementAccepted && !pollingPayment && (
        <Card className="bg-navy-900/50 border-slate-800">
          <CardContent className="py-6">
            <div className="text-center">
              <CreditCard className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="text-white font-medium mb-2">Ready to Begin</h3>
              <p className="text-slate-400 text-sm mb-4">
                Complete your payment to start your A.I. Readiness Consultation
              </p>
              <Button
                onClick={handlePayNow}
                disabled={initiatingCheckout}
                className="bg-primary hover:bg-primary/90"
                data-testid="pay-now-btn"
              >
                {initiatingCheckout ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Pay {servicePrice.display} – Get Started
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Call Modal */}
      <RequestCallModal
        isOpen={showRequestCallModal}
        onClose={() => setShowRequestCallModal(false)}
        onSubmit={handleRequestCall}
        isSubmitting={submittingCallRequest}
      />
    </div>
  );
};

export default ServiceAProgressPanel;
